import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  createNewUserPromise,
  getManagementUserPromise,
  type GetManagementUserSchema,
  LOG_ACTION_TYPE_MAP,
} from 'src/utils/api';
import { useNotification, useLogAction } from 'src/hooks';
import { useAddInternalUserFlag } from 'src/hooks/api/use-manage-internal-user-flag';
import isEmpty from 'lodash/isEmpty';
import { QUERY_KEY_MAP } from 'src/constants';

export const useCreateNewUser = () => {
  const queryClient = useQueryClient();
  const { logUserAction } = useLogAction();
  const { addInternalUserFlagAsync } = useAddInternalUserFlag();

  const { mutateAsync, isPending, isError } = useMutation({
    mutationFn: createNewUserPromise,
  });

  const { enqueueErrorNotification, enqueueSuccessNotification } = useNotification();

  const createNewUser = async (user: GetManagementUserSchema) => {
    try {
      const res = await mutateAsync({
        ...(user || {}),
        stores: user.stores.map((store) => store.uuid),
        orgs: user.orgs.map((org) => org.uuid),
        role: user.role.map((role) => role.user_role),
      });

      if (res) {
        await logUserAction({
          action: 'create',
          action_from: '',
          action_to: '',
          type: LOG_ACTION_TYPE_MAP.USER_MANAGEMENT,
          type_metadata: JSON.stringify({
            uuid: user.uuid,
          }),
        });

        enqueueSuccessNotification('User created successfully');

        void queryClient.invalidateQueries({
          queryKey: [QUERY_KEY_MAP.useListExternalUsers],
        });

        void queryClient.invalidateQueries({
          queryKey: [QUERY_KEY_MAP.useListAuditLogs],
        });
      } else {
        enqueueErrorNotification('Error creating user');
      }
      return res;
    } catch (error) {
      enqueueErrorNotification((error as Error).message || 'Error creating new user');
      return false;
    }
  };

  const cloneUser = async ({
    uuid_to_clone,
    clone_username,
    clone_password,
    uuid,
    full_name,
    is_internal_user,
  }: {
    uuid_to_clone: string;
    clone_username: string;
    clone_password: string;
    uuid: string;
    full_name: string;
    is_internal_user: boolean;
  }) => {
    try {
      if (is_internal_user) {
        const internalUserFlagRes = await addInternalUserFlagAsync(clone_username);

        if (!internalUserFlagRes) {
          enqueueErrorNotification('Error adding internal user flag');
          return false;
        }
      }

      const userToCloneDetails = await getManagementUserPromise(uuid_to_clone);

      if (isEmpty(userToCloneDetails)) {
        enqueueErrorNotification('User to clone not found');
        return false;
      }

      const res = await mutateAsync({
        ...userToCloneDetails,
        uuid,
        user_real_name: full_name || userToCloneDetails.user_real_name,
        user_name: clone_username,
        user_email: clone_username,
        user_password: clone_password,
        stores: userToCloneDetails.stores.map((store) => store.uuid),
        orgs: userToCloneDetails.orgs.map((org) => org.uuid),
        role: userToCloneDetails.role.map((role) => role.user_role),
      });

      if (res) {
        await logUserAction({
          action: 'clone',
          action_from: '',
          action_to: '',
          type: LOG_ACTION_TYPE_MAP.USER_MANAGEMENT,
          type_metadata: JSON.stringify({
            uuid_to_clone,
            uuid,
          }),
        });

        enqueueSuccessNotification('User cloned successfully');

        void queryClient.invalidateQueries({
          queryKey: [QUERY_KEY_MAP.useListExternalUsers],
        });

        void queryClient.invalidateQueries({
          queryKey: [QUERY_KEY_MAP.useListAuditLogs],
        });
      } else {
        enqueueErrorNotification('Error cloning user');
      }

      return res;
    } catch (error) {
      enqueueErrorNotification((error as Error).message || `Error cloning user ${uuid_to_clone}`);
      return false;
    }
  };

  return {
    createNewUser,
    cloneUser,
    isLoading: isPending,
    isError,
  };
};
